import React, { useState } from 'react';
import { FiX, FiCheck, FiShield } from 'react-icons/fi';
import { getAuthHeaders } from '../utils/auth';
import { getApiEndpoint } from '../utils/api';
import './ConsentModal.css';

const CONSENT_VERSION = '2026-04-20';

export default function ConsentModal({ isOpen, onClose, onDecision }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const submit = async (decision) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(getApiEndpoint('/api/auth/consent'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ decision, consent_version: CONSENT_VERSION }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to save consent decision');
      }
      const data = await res.json();
      onDecision(decision, data);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="consent-modal-overlay" onClick={() => !loading && onClose()}>
      <div className="consent-modal" onClick={e => e.stopPropagation()}>
        <div className="consent-modal-header">
          <h3><FiShield /> Share submissions to improve reviewer quality?</h3>
          <button className="consent-modal-close" onClick={onClose} disabled={loading}>
            <FiX />
          </button>
        </div>
        <div className="consent-modal-body">
          <p>
            Mnevia's reviewers get sharper when they can compare your draft to
            real proposals that have been through study sections. You can help
            us build that reference set by letting us include de-identified
            versions of submissions you explicitly mark for contribution.
          </p>
          <ul className="consent-modal-list">
            <li>
              <strong>Your choice per upload.</strong> Consent here unlocks the
              option — each submission still needs its own checkbox to be
              included.
            </li>
            <li>
              <strong>De-identification.</strong> PI names, institutions, grant
              numbers, and other identifying markers are scrubbed before a
              submission enters the reference set.
            </li>
            <li>
              <strong>Withdraw anytime.</strong> You can revoke consent from
              Settings; we'll remove your contributions from future retrieval.
            </li>
          </ul>
          <p className="consent-modal-fineprint">
            Version {CONSENT_VERSION}. This does not affect how your private
            proposal appears to you or collaborators — only what enters the
            shared reviewer-calibration corpus.
          </p>
          {error && <div className="consent-modal-error">{error}</div>}
        </div>
        <div className="consent-modal-footer">
          <button
            className="consent-modal-btn consent-modal-btn--decline"
            onClick={() => submit('denied')}
            disabled={loading}
          >
            No thanks
          </button>
          <button
            className="consent-modal-btn consent-modal-btn--accept"
            onClick={() => submit('granted')}
            disabled={loading}
          >
            <FiCheck /> {loading ? 'Saving…' : 'Yes, include my marked submissions'}
          </button>
        </div>
      </div>
    </div>
  );
}
